import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, ShoppingBag, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import { useCart } from "@/context/CartContext";
import { useQuote } from "@/components/QuoteDialog";

const LINKS = [
  { to: "/sectors/care-homes", label: "Care Homes", id: "care" },
  { to: "/sectors/schools", label: "Schools", id: "schools" },
  { to: "/sectors/corporate", label: "Corporate", id: "corporate" },
  { to: "/chefs", label: "Chef Agency", id: "chefs" },
  { to: "/flapjack", label: "Flapjack Shop", id: "flapjack" },
  { to: "/resources", label: "Resources", id: "resources" },
];

export const Nav = () => {
  const { count, setOpen } = useCart();
  const { openQuote } = useQuote();
  const [menu, setMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const solid = scrolled || menu || pathname === "/flapjack";

  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 30);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,padding] duration-500 ${solid ? "bg-[#141412]/85 backdrop-blur-xl py-3 border-b border-white/10" : "bg-transparent py-5"}`}
      data-testid="site-header"
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 sm:px-8">
        <Link to="/" data-testid="nav-logo-link" onClick={() => setMenu(false)}>
          <Logo />
        </Link>
        <nav className="hidden xl:flex items-center gap-7">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.id}-link`}
              className={({ isActive }) =>
                `group relative text-sm font-medium transition-colors ${isActive ? "text-[#C59B27]" : "text-stone-200 hover:text-white"}`
              }
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-full origin-left scale-x-0 bg-[#C59B27] transition-transform duration-500 group-hover:scale-x-100" />
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={() => setOpen(true)}
            className="relative grid h-11 w-11 place-items-center rounded-full border border-white/20 text-white transition-colors hover:bg-white/10"
            data-testid="nav-cart-button"
            aria-label="Open cart"
          >
            <ShoppingBag className="h-[18px] w-[18px]" />
            {count > 0 && (
              <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-[#C59B27] px-1 text-[11px] font-bold text-[#141412]" data-testid="nav-cart-count">
                {count}
              </span>
            )}
          </button>
          <button onClick={() => openQuote()} className="btn-gold max-sm:!hidden !py-2.5 !px-5 text-sm" data-testid="nav-quote-button">
            Request a quote
          </button>
          <button
            className="grid h-11 w-11 place-items-center rounded-full border border-white/20 text-white xl:hidden"
            onClick={() => setMenu((m) => !m)}
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {menu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {menu && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden xl:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-5 pb-6 pt-4">
              {LINKS.map((l, i) => (
                <motion.div key={l.to} initial={{ x: -20, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.05 * i }}>
                  <Link to={l.to} onClick={() => setMenu(false)} className="block py-3 font-display text-3xl text-[#FAF8F5]" data-testid={`mobile-nav-${l.id}-link`}>
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <button onClick={() => { setMenu(false); openQuote(); }} className="btn-gold mt-4 justify-center" data-testid="mobile-nav-quote-button">
                Request a quote
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
